// ── BCHNostr Verified Badge ───────────────────────────────────────
//
// The original BCHNostr verified mark: an emerald scalloped seal with a
// checkmark. Kept in one place so every surface (posts, profiles,
// comments, livestream chat, search results, stream cards…) renders the
// identical design.
//
// Rendering the badge is ALWAYS gated on real verification — see
// `useNip05Verified`. Never render it from the presence of a `nip05`
// field alone: that's a self-asserted claim, and treating it as proof
// hands out the paid badge for free.

import { useNip05Verified, useVerifiedHandle } from '../hooks/useNip05Verified';

export type VerifiedBadgeSize = 'xs' | 'sm' | 'md';

const SIZE_CLASS: Record<VerifiedBadgeSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
};

/**
 * The raw seal mark. Use this only when verification has already been
 * confirmed by the caller; otherwise prefer `<VerifiedBadge>`.
 */
export function VerifiedCheck({
  size = 'sm', className = '',
}: { size?: VerifiedBadgeSize; className?: string }) {
  return (
    <svg
      className={`${SIZE_CLASS[size]} ${className}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export interface VerifiedBadgeProps {
  /** The user's claimed NIP-05, from their kind 0 profile. */
  nip05: string | undefined | null;
  /** The pubkey the claim must resolve to. */
  pubkey: string | undefined;
  size?: VerifiedBadgeSize;
  /** Show a hover tooltip naming the verified identifier. */
  showTooltip?: boolean;
  className?: string;
}

/**
 * Verification-gated badge. Renders nothing unless the BCHNostr NIP-05
 * directory confirms `nip05` → `pubkey`.
 *
 * This is the component to use anywhere a name is displayed.
 */
export default function VerifiedBadge({
  nip05, pubkey, size = 'sm', showTooltip = true, className = '',
}: VerifiedBadgeProps) {
  const verified = useNip05Verified(nip05, pubkey);
  // When the profile still carries the claim, name it in the tooltip;
  // otherwise fall back to the directory-resolved handle (profiles can be
  // re-published with a blank nip05 while the paid badge remains valid).
  const resolvedHandle = useVerifiedHandle(pubkey);
  const label = nip05 || resolvedHandle || '';
  if (!verified) return null;

  return (
    <span
      title={showTooltip ? `Verified: ${label}` : undefined}
      aria-label={`Verified BCHNostr account: ${label}`}
      className={`inline-flex shrink-0 items-center text-emerald-500 ${
        showTooltip ? 'cursor-help' : ''
      } ${className}`}
    >
      <VerifiedCheck size={size} />
    </span>
  );
}

/**
 * Pill form with a "Verified" label, for profile headers and other
 * places with room for more than the mark alone.
 */
export function VerifiedPill({
  nip05, pubkey, className = '',
}: { nip05: string | undefined | null; pubkey: string | undefined; className?: string }) {
  const verified = useNip05Verified(nip05, pubkey);
  const resolvedHandle = useVerifiedHandle(pubkey);
  const label = nip05 || resolvedHandle || '';
  if (!verified) return null;

  return (
    <div
      title={`Verified: ${label}`}
      className={`inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 ${className}`}
    >
      <VerifiedCheck size="md" />
      <span>Verified</span>
    </div>
  );
}
